import { DataGrid } from "Common/CusomReactGrid/CustomDataGrid";
import { addPatient } from "Redux/TabsSlice/TabsSlice";
import NumberConversion from "Utils/NumberConversion";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import CardPaymentPopup from "../PatientPaymentDetail/CardPaymentPopup/CardPaymentPopup";
import StatementPopup from "Popups/StatementPoup/StatementPopup";
import Dropdown from "Common/DropDown/Dropdown";
import ThreeDotsIcon from "SVGS/ThreeDotsIcon";
import { PAYMENT_METHODS } from "Common/Constants";
import { Tooltip } from "react-tooltip";
import { CellExpanderFormatter } from "Components/CellExpanderFormatter/CellExpanderFormatter";
import PaymentDetailView from "./PaymentDetailView";
import FormatDate from "Utils/FormatDate";
import api from "http";
import ConfirmationPopup from "Popups/ConfirmationPopup/ConfirmationPopup";
import { toast } from "react-toastify";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import FullPageLoader from "Components/FullPageLoader/FullPageLoader";

const paymentTypeColors = {
  PATIENT:
    "font-semibold cursor-pointer hover:text-primary transition-all-ease-in-out",
  ERA: "text-red-500 hover:font-semibold transition-all-ease-in-out hover:underline",
  EOB: "text-teal-500 hover:font-semibold transition-all-ease-in-out hover:underline",
};

const PatientPaymentSearchGrid = ({
  rows,
  handlePayment,
  handlePerRowsChange,
  handlePageChange,
  paramModel,
  allowPatientCol,
  onRowsChange,
  onRefresh,
}) => {
  const [paymentPopup, setPaymentPopup] = useState({
    status: false,
    rowData: null,
  });
  const [openStatementPopup, setOpenStatementPopup] = useState({
    rowData: null,
    status: false,
  });
  const [deletePaymentPopup, setDeletePaymentPopup] = useState({
    status: false,
    paymentId: null,
  });
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const handleOpenStatement = (row) => {
    setOpenStatementPopup({
      rowData: row,
      status: true,
    });
  };

  const goToPatient = (row, tabname) => {
    const obj = {
      active: true,
      id: row.patientId,
      subTab: tabname,
      name: row.patientName,
      unsavedChanges: false,
      insuranceData: { status: false, editMode: false },
    };
    dispatch(addPatient(obj));
  };

  const handleRowPayment = (row) => {
    handlePayment(row);
  };

  const closePaymentPopup = (row) => {
    setPaymentPopup({
      status: false,
      rowData: null,
    });
  };

  const handleOpenDeletePopup = (paymentId) => {
    setDeletePaymentPopup({
      status: true,
      paymentId: paymentId,
    });
  };

  const handleDeletePayment = async (inActiveReason) => {
    try {
      setLoader(true);
      const payload = {
        ids: [deletePaymentPopup.paymentId],
        inActive: true,
        practiceId: 0,
        inActiveReason: inActiveReason || "",
      };
      const resp = await api.post(
        `${process.env.REACT_APP_TC_PMS}/api/Payment/DeletePaymentMain`,
        payload
      );
      toast.success(resp.data.responseMessage || "Payment deleted successfully");
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setDeletePaymentPopup({
        status: false,
        paymentId: null,
      });
      setLoader(false);
    }
  };

  const handleCancelDelete = () => {
    setDeletePaymentPopup({
      status: false,
      paymentId: null,
    });
  };

  const columns = [
    {
      key: "expanded",
      name: "",
      minWidth: 40,
      width: 20,
      colSpan(arguments_) {
        return arguments_.type === "ROW" && arguments_.row.type === "DETAIL"
          ? columns.length
          : undefined;
      },
      cellClass(row) {
        return row.type === "DETAIL" ? "bg-slate-50" : undefined;
      },
      formatter({ row, isCellSelected, onRowChange }) {
        if (row.type === "DETAIL") {
          return (
            <div className="h-full overflow-x-hidden overflow-y-auto bg-slate-50">
              <PaymentDetailView row={row} onRefresh={onRefresh} />
            </div>
          );
        }

        return (
          <CellExpanderFormatter
            expanded={row.expanded}
            tabIndex={0}
            onCellExpand={() => {
              onRowChange({ ...row, expanded: !row.expanded });
            }}
          />
        );
      },
    },
    {
      name: "Payment ID",
      key: "paymentType",
      formatter: ({ row }) => {
        return (
          <button
            onClick={() => handleRowPayment(row)}
            className={` font-semibold cursor-pointer hover:text-primary px-2 py-[.1rem]  rounded-full ${
              paymentTypeColors[row.paymentType]
            }`}
          >
            {row.paymentId}{" "}
          </button>
        );
      },
    },

    {
      // width: 130,
      name: "Patient Name",
      width: 130,
      key: "patientName",
    },
    {
      // width: 130,
      name: "Service Location",
      width: 130,
      key: "locationName",
    },
    {
      width: 100,
      name: "Amount",
      key: "amount",
      formatter: ({ row }) => (
        <span className="text-sm font-semibold">
          {NumberConversion(row.amount)}
        </span>
      ),
    },

    {
      width: 100,
      name: "Date Received",
      key: "receivedDate",
      formatter: ({ row }) => <span>{FormatDate(row.receivedDate)}</span>,
    },

    { width: 120, name: "Payment Method", key: "paymentMethod" },
    {
      width: 100,
      name: "Unallocated",
      key: "unAllocatedAmount",
      formatter: ({ row }) => (
        <span>{NumberConversion(row.unAllocatedAmount)}</span>
      ),
    },
    {
      width: 100,
      name: (
        <div
          data-tooltip-id="pendingClaims_tooltip"
          data-tooltip-content="Number of claims pending insurance processing"
        >
          Pending Claims
        </div>
      ),
      key: "pendingClaims",
      formatter: ({ row }) => {
        const count = row.pendingClaims;
      
        return (
          <span>
            {count > 0 ? count : ""}
          </span>
        );
      },
    },
    {
      width: 100,
      name: "Status",
      key: "isPosted",
      formatter: ({ row }) => {
        const isUnpostable = row.isUnpostable;
        const isPosted = row.isPosted;
        const pillClass = isUnpostable
          ? "status-pill-ready"
          : isPosted
            ? "status-pill-paid"
            : "status-pill-pending";
        const label = isUnpostable
          ? "Unpostable"
          : isPosted
            ? "Posted"
            : "Unposted";
        return (
          <div className={`status-pill ${pillClass}`}>
            {label}
          </div>
        );
      },
    },
    {
      width: 120,
      name: "Payment Source",
      key: "paymentSource",
      formatter: ({ row }) => (
        <span>
          {row.paymentMethod === PAYMENT_METHODS.CASH ||
          row.paymentMethod === PAYMENT_METHODS.CHECK ? (
            <span className="flex items-center h-full">External </span>
          ) : (
            <span className="flex items-center h-full">
              {row.paymentSource}{" "}
            </span>
          )}
        </span>
      ),
    },
    // {
    //     width: 140, name: "Patient Due Claims", key: "patientDueClaims",
    //     formatter: ({ row }) => <span>  {row.patientDueClaims}/{row.totalVisits}</span>,
    // },
    // {
    //     name: "Memo", key: "memo",width: 250,
    //     formatter: ({ row }) => (
    //         <span
    //             data-tooltip-id="patientPaymentMemo_tooltip"
    //             data-tooltip-content={row.memo}
    //             className="block max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap"
    //         >
    //             {row.memo}
    //         </span>
    //     )
    // },

    {
      name: "Added By",
      key: "addedBy",
      width: 180,
    },

    {
      width: 80,
      name: "Action",
      key: "",
      formatter: ({ row }) => {
        return (
          <div className="flex items-center justify-center h-full">
            <Dropdown>
              <Dropdown.Button>
                <ThreeDotsIcon />
              </Dropdown.Button>
              <Dropdown.Menu>
                {allowPatientCol ? (
                  <>
                    <Dropdown.MenuItem
                      className="group rdx_item"
                      onSelect={() => goToPatient(row, "Overview")}
                    >
                      Overview
                    </Dropdown.MenuItem>
                    <Dropdown.MenuItem
                      className="group rdx_item"
                      onSelect={() => goToPatient(row, "Claims")}
                    >
                      Claims
                    </Dropdown.MenuItem>
                    <Dropdown.MenuItem
                      className="group rdx_item"
                      onSelect={() => goToPatient(row, "PatientPayments")}
                    >
                      Patient Payments
                    </Dropdown.MenuItem>
                    <Dropdown.MenuItem
                      className="group rdx_item"
                      onSelect={() => goToPatient(row, "Overview")}
                    >
                      {row.paymentFrom}
                    </Dropdown.MenuItem>
                    <Dropdown.MenuItem
                      className="group rdx_item"
                      onSelect={() => handleOpenStatement(row)}
                    >
                      Link with Statement
                    </Dropdown.MenuItem>
                    {(row.paymentMethod === PAYMENT_METHODS.CASH ||
                      row.paymentMethod === PAYMENT_METHODS.CHECK) &&
                      row.amount === row.unAllocatedAmount && (
                        <Dropdown.MenuItem
                          className="group rdx_item"
                          onSelect={() => handleOpenDeletePopup(row.paymentId)}
                        >
                          Delete
                        </Dropdown.MenuItem>
                      )}
                  </>
                ) : (
                  <>
                    <Dropdown.MenuItem
                      className="group rdx_item"
                      onSelect={() => handleOpenStatement(row)}
                    >
                      Link with Statement
                    </Dropdown.MenuItem>
                    {(row.paymentMethod === PAYMENT_METHODS.CASH ||
                      row.paymentMethod === PAYMENT_METHODS.CHECK) &&
                      row.amount === row.unAllocatedAmount && (
                        <Dropdown.MenuItem
                          className="group rdx_item"
                          onSelect={() => handleOpenDeletePopup(row.paymentId)}
                        >
                          Delete
                        </Dropdown.MenuItem>
                      )}
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      },
      cellClass: "showDropdownInGrid",
    },
  ].filter((v) => v);
  return (
    <>
      {loader && <FullPageLoader allowFullScreen />}
      {paymentPopup.status && (
        <CardPaymentPopup
          onClose={closePaymentPopup}
          paymentModel={paymentPopup.rowData}
        />
      )}
      {openStatementPopup.status && (
        <StatementPopup
          data={openStatementPopup.rowData}
          onClose={() => setOpenStatementPopup({ status: false })}
        />
      )}
      {deletePaymentPopup.status && (
        <ConfirmationPopup
          heading="Delete Payment"
          subHeading="Are you sure you want to delete this payment?"
          reasonBox={true}
          required={true}
          onConfirm={handleDeletePayment}
          onCancel={handleCancelDelete}
          buttonTitle="Delete"
          cancelButtonTitle="Cancel"
        />
      )}
      <Tooltip
        opacity={1}
        style={{
          zIndex: 12,
          minWidth: 150,
          maxWidth: 450,
          boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
        }}
        id="patientPaymentMemo_tooltip"
      />
      <Tooltip
        id={`refundDisabled_tooltip`}
        style={{ zIndex: 99, maxWidth: 220 }}
      />
      <Tooltip id="pendingClaims_tooltip" style={{ zIndex: 2}} />
      <Tooltip
        opacity={1}
        style={{
          zIndex: 12,
          minWidth: 150,
          maxWidth: 450,
          boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
          whiteSpace: "normal",
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}
        id="paymentDetailMemo_tooltip"
      />

      <DataGrid
        formId="patientPaymentSearchGrid"
        gridHeightOffset={allowPatientCol ? 270 : 230}
        contentBasedHeight={!allowPatientCol}
        contentBasedMaxHeight={400}
        paginationDefaultPage={1}
        columns={columns}
        rows={rows}
        paginationPerPage={paramModel.perPage}
        pagination={{
          perPage: paramModel.perPage,
          pageNo: paramModel.pageNo,
          totalCount: paramModel.totalCount,
        }}
        manualPagination={true}
        handlePerRowsChange={handlePerRowsChange}
        handlePageChange={handlePageChange}
        onRowsChange={onRowsChange}
        rowHeight={(row) => (row.type === "DETAIL" ? 57 : 40)}
        paginationTotalRows={paramModel.totalCount}
        // gridHeightOffset={400}
      />
    </>
  );
};

export default PatientPaymentSearchGrid;

PatientPaymentSearchGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  handlePayment: PropTypes.func.isRequired,
  handlePerRowsChange: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  paramModel: PropTypes.object.isRequired,
  allowPatientCol: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func,
};
